import { useCallback, useRef } from 'react';
import { createSearchParams, useSearchParams } from 'react-router';

import { useDeferredNavigate } from 'hooks/useDeferredNavigate';
import { useLatest } from 'hooks/useLatest';
import {
  SEARCH_JOBS_FILTERS,
  SearchJobsFilterKeys,
} from 'modules/search/constants/filters/searchJobsFilters';

export function useSearchFilters() {
  const filtersRef = useRef<HTMLFormElement | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useDeferredNavigate();
  const searchParamsRef = useLatest<URLSearchParams>(searchParams);

  const areFiltersActive = SEARCH_JOBS_FILTERS.some((filter) =>
    searchParams.has(filter),
  );

  const clearAllFilters = useCallback(() => {
    ([...searchParamsRef.current.keys()] as SearchJobsFilterKeys[]).forEach(
      (key) => {
        if (SEARCH_JOBS_FILTERS.includes(key)) {
          searchParamsRef.current.delete(key);
        }
      },
    );

    const newSearchParams = createSearchParams(searchParamsRef.current);

    navigate(`?${newSearchParams}`, { replace: true });

    filtersRef.current
      ?.querySelectorAll('[data-facet-state="PINNED"]')
      .forEach((node) => {
        node.setAttribute('data-facet-state', 'UNPINNED');
      });

    filtersRef.current
      ?.querySelectorAll('input[type="checkbox"]')
      .forEach((option) => {
        if (option instanceof HTMLInputElement) {
          // eslint-disable-next-line no-param-reassign
          option.checked = false;
        }
      });

    filtersRef.current
      ?.querySelectorAll('input[type="radio"]')
      .forEach((option) => {
        if (option instanceof HTMLInputElement) {
          // eslint-disable-next-line no-param-reassign
          option.checked = Boolean(
            option.value === '' && option.type === 'radio',
          );
        }
      });
  }, [navigate, searchParamsRef]);

  return {
    clearAllFilters,
    filtersRef,
    areFiltersActive,
  };
}
